/**
 * hdphotohub.js — Carousel Composer: a delivered HDPhotoHub shoot as a photo source.
 *
 * Nothing is uploaded, proxied or cached on our side. Tiles use the portal's own
 * 480px thumbnail endpoint; cell previews and the export read the original straight
 * from media.hd.pics, which sends `access-control-allow-origin: *`, so a canvas
 * drawn from it is not tainted. Those URLs rotate, so every record also carries a
 * same-origin fallback through the media download route.
 *
 * Dimensions start as the thumbnail's (framing is ratio-based, and the export
 * re-measures the decoded original), then upgrade to the real ones the first time
 * the original is decoded — see previews.js and the store's updatePhotoDims.
 */
import { bySequence } from './library.js';

export const shootThumbUrl = (mid) => '/api/media/hdphotohub/thumb/' + mid;
export const shootFullFallbackUrl = (mid) => '/api/media/download/hdphotohub/' + mid;

const MEASURE_CONCURRENCY = 6;

// "1429 Shoreline Dr" -> "1429 SHORELINE": short enough for a tile caption and a
// filter chip, still unmistakable when two shoots are loaded side by side.
export function shootFam(site) {
  const parts = String(site.address || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'SHOOT ' + (site.sid || '');
  const num = /^\d+$/.test(parts[0]) ? parts[0] : '';
  const word = (num ? parts[1] : parts[0]) || '';
  return ((num ? num + ' ' : '') + word).toUpperCase().slice(0, 18);
}

export function shootLabel(site) {
  return [site.address, site.city].filter(Boolean).join(', ') || ('Shoot ' + site.sid);
}

// HDPhotoHub sends "8/3/2026 9:50:31 PM"
export function shootDate(site) {
  const d = site.created ? new Date(site.created) : null;
  return d && !Number.isNaN(d.getTime()) ? d : null;
}

export function shootStills(site) {
  return (site.media || []).filter((m) => m.type === 'image');
}

// HDPhotoHub puts delivered photos to sleep: the item keeps its name in the
// catalog but serves a placeholder. Since 2026-09-30 the server points those at
// the delivery CDN's original instead, so a sleeping shoot still loads in full —
// the count is why it is SLOWER (originals are ~4x a woken derivative), not why
// it is short. Waking, which restores the light versions and the client-facing
// gallery, is done in HDPhotoHub: their API has no endpoint for it.
export function shootAsleep(site) {
  return site && site.asleepCount > 0 ? site.asleepCount : 0;
}

// The hub's Site Media page for this shoot — where "Wake Up Media" lives. The
// server builds it (the hub domain is its env), so an older cached payload
// without one simply gets no link.
export function shootManageUrl(site) {
  return (site && site.manageUrl) || null;
}

// "1429 Shoreline Dr-01" -> "01". Delivery order is the only number that means
// anything here: these are renamed on delivery, not camera files.
function deliveryNo(name, i) {
  const m = String(name || '').match(/(\d+)\s*$/);
  return m ? m[1] : String(i + 1);
}

function measure(url) {
  return new Promise((res) => {
    const img = new Image();
    let done = false;
    const finish = (v) => { if (!done) { done = true; res(v); } };
    img.onload = () => finish({ w: img.naturalWidth || 3, h: img.naturalHeight || 2 });
    img.onerror = () => finish(null);
    img.src = url;
    setTimeout(() => finish(null), 15000);
  });
}

export function shootPhotoRecord(site, m, i, uid, dims) {
  const no = deliveryNo(m.name, i);
  return {
    id: uid(),
    name: m.name || (shootLabel(site) + '-' + no),
    url: m.url,
    fallbackUrl: shootFullFallbackUrl(m.mid),
    thumb: shootThumbUrl(m.mid),
    w: dims ? dims.w : 3,
    h: dims ? dims.h : 2,
    exactDims: false,
    crossOrigin: true,
    fam: shootFam(site),
    seq: parseInt(no, 10) || i + 1,
    seqStr: no,
    variant: '',
    source: 'hdph',
    mid: m.mid,
    sid: site.sid,
    shoot: shootLabel(site),
  };
}

// Loads a shoot's stills as library records. Photos already in `existing` (same
// mid) are skipped, so re-opening a shoot never duplicates it.
// onProgress(done, total, loadedSoFar) fires per photo, like loadImageFiles.
export async function loadShootPhotos(site, { uid, onProgress, existing = [] } = {}) {
  const have = new Set(existing.filter((im) => im.source === 'hdph').map((im) => im.mid));
  const list = shootStills(site).filter((m) => !have.has(m.mid));
  if (!list.length) return [];
  const out = [];
  let done = 0, next = 0;
  if (onProgress) onProgress(0, list.length, out);
  async function worker() {
    while (next < list.length) {
      const i = next++;
      const m = list[i];
      // the thumbnail is small, same-origin and already cached server-side
      const dims = await measure(shootThumbUrl(m.mid));
      out.push(shootPhotoRecord(site, m, i, uid, dims));
      done++;
      if (onProgress) onProgress(done, list.length, out);
    }
  }
  await Promise.all(Array.from({ length: Math.min(MEASURE_CONCURRENCY, list.length) }, worker));
  return out.sort(bySequence);
}
